import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Building2, KeyRound, ArrowLeft, Languages } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { useApp } from "../../contexts/AppContext";
import { useVerifySignupOTPMutation, useSendSignupOTPMutation } from "../../store/api/auth";
import { setUser } from "../../store/slices/authSlice";
import storage from "../../utils/storage";
import { STORAGE_KEYS, WORKSPACE_DEFAULT_TIMEZONE } from "../../utils/constants";
import { handleApiError } from "../../utils/apiErrors";

export default function VerifySignupOTPPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const { setAuthUser } = useApp();
  
  const [verifyOTP, { isLoading: isVerifying }] = useVerifySignupOTPMutation();
  const [resendOTP, { isLoading: isResending }] = useSendSignupOTPMutation();
  
  // Get data from navigation state
  const { email, workspaceName, userName, timezone } = location.state || {};
  const browserTimeZone = timezone || WORKSPACE_DEFAULT_TIMEZONE;
  
  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Redirect if no email
  useEffect(() => {
    if (!email || !workspaceName || !userName) {
      toast.error(t('auth.invalidSession'));
      navigate('/signup');
    }
  }, [email, workspaceName, userName, navigate, t]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const toggleLanguage = () => {
    setLanguage(language === 'ja' ? 'en' : 'ja');
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp) {
      toast.error(t('auth.enterOtpRequired'));
      return;
    }

    if (otp.length !== 6) {
      toast.error(t('auth.otpLength'));
      return;
    }

    try {
      const resp: any = await verifyOTP({
        email,
        otp,
        workspaceName,
        userName,
        timezone: browserTimeZone,
      });
      
      if (resp.data) {
        // Store tokens
        storage.set(STORAGE_KEYS.ACCESS_TOKEN, resp.data.access_token);
        if (resp.data.refresh_token) {
          storage.set(STORAGE_KEYS.REFRESH_TOKEN, resp.data.refresh_token);
        }
        
        // Store user info
        storage.set(STORAGE_KEYS.USER_INFO, resp.data.user);
        
        // Store current workspace from user data if available
        if (resp.data.user?.currentWorkspaceId) {
          storage.set(STORAGE_KEYS.CURRENT_WORKSPACE, resp.data.user.currentWorkspaceId);
        }
        
        // Update Redux state
        dispatch(setUser(resp.data.user));
        
        // Update AppContext state
        setAuthUser(resp.data.user);
        
        toast.success(t('auth.signupSuccess'));
        navigate('/');
      }
      
      if (resp.error) {
        handleApiError(resp.error, t('auth.otpInvalidOrExpired'));
      }
    } catch (error: any) {
      console.error('Verify signup OTP error:', error);
      handleApiError(error, t('auth.otpInvalidOrExpired'));
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    try {
      const resp: any = await resendOTP({ email });
      
      if (resp.data) {
        toast.success(t('auth.otpResent'));
        setCountdown(60);
        setCanResend(false);
      }
      
      if (resp.error) {
        handleApiError(resp.error, t('auth.otpResendFailed'));
      }
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      handleApiError(error, t('auth.otpResendFailed'));
    }
  };

  const handleBackToSignup = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* Language toggle button */}
      <div className="absolute top-4 right-4">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLanguage}
          className="gap-2"
        >
          <Languages className="w-4 h-4" />
          {language === 'ja' ? t('auth.switchToEnglish') : t('auth.switchToJapanese')}
        </Button>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            {t('auth.verifyOtpTitle')}
          </CardTitle>
          <CardDescription>
            {t('auth.verifyOtpDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('auth.workspaceName')}:</span>
              <span className="font-medium text-gray-900">{workspaceName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('auth.yourName')}:</span>
              <span className="font-medium text-gray-900">{userName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('auth.email')}:</span>
              <span className="font-medium text-gray-900">{email}</span>
            </div>
          </div>

          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="mb-2" htmlFor="otp">
                  {t('auth.otpLabel')}
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToSignup}
                  className="h-auto p-0 text-xs"
                >
                  <ArrowLeft className="w-3 h-3 mr-1" />
                  {t('common.back')}
                </Button>
              </div>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="otp"
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="pl-10 text-center tracking-widest"
                  disabled={isVerifying}
                  maxLength={6}
                  autoFocus
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isVerifying || otp.length !== 6}>
              {isVerifying ? t('auth.creating') : t('auth.createWorkspaceButton')}
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResendOTP}
              className="w-full"
              disabled={!canResend || isResending}
            >
              {isResending 
                ? t('auth.sending')
                : canResend 
                  ? t('auth.resendOtp')
                  : t('auth.resendOtpCountdown', { seconds: countdown })
              }
            </Button>
          </form>

          <div className="mt-4 text-center text-xs text-gray-500">
            <p>
              {t('auth.workspaceOwnerNote')}
              <br />
              {t('auth.workspaceOwnerNote2')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

